/**
 * MatterAiWorkspace — route shell for the Phase 1a AI workspace.
 *
 * Route: /matter/:matterRef/ai-workspace
 *
 * Belt-and-braces feature gate:
 *   - Backend returns 404 from every route if AI_WORKSPACE_ENABLED is
 *     off or the user isn't in the alpha allowlist.
 *   - Frontend also hides the entry surface (and rejects direct
 *     navigation) on the same conditions.  A flag-off user typing the
 *     URL gets a clean "Not available" screen, NOT a broken render.
 *
 * Phase 1a four-pane layout:
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ Header                                                        │
 *   ├──────────────┬───────────────────────────────────────────────┤
 *   │ ActionBar    │ AnswerPane                                     │
 *   │              │                                                │
 *   │ DocumentRail │                                                │
 *   │              │                                                │
 *   ├──────────────┴───────────────────────────────────────────────┤
 *   │ FollowUpInput                                                 │
 *   └──────────────────────────────────────────────────────────────┘
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../../config/api";
import { errorMessage as formatError } from "../../utils/errorMessage";
import { Button } from "../../components/ui/button";
import { ChevronLeft, Sparkles } from "lucide-react";
import { toast } from "sonner";

import ActionBar from "./ActionBar";
import DocumentRail from "./DocumentRail";
import AnswerPane from "./AnswerPane";
import FollowUpInput from "./FollowUpInput";

const VANESSA_EMAIL = "vanessa@bonsai.law";

function isWorkspaceEnabled() {
    // Two-layer check:
    //   1. Env flag (Vite/CRA injects REACT_APP_AI_WORKSPACE_ENABLED at build time).
    //      Default is OFF — if the flag isn't explicitly "true", we hide the page.
    //   2. Per-user allowlist match.  Phase 1a is Vanessa-only.
    const flagOn = (process.env.REACT_APP_AI_WORKSPACE_ENABLED || "").trim().toLowerCase() === "true";
    if (!flagOn) return false;
    const stored = localStorage.getItem("user");
    if (!stored) return false;
    try {
        const user = JSON.parse(stored);
        const email = (user.email || user.user_email || "").trim().toLowerCase();
        return email === VANESSA_EMAIL;
    } catch {
        return false;
    }
}

function buildAuthHeaders() {
    const stored = localStorage.getItem("user");
    let email = "";
    try {
        const user = stored ? JSON.parse(stored) : {};
        email = user.email || user.user_email || "";
    } catch {
        email = "";
    }
    return {
        "Content-Type": "application/json",
        "X-User-Email": email,
        "X-Bonsai-User-Id":
            sessionStorage.getItem("bonsai_user_id") ||
            localStorage.getItem("bonsai_user_id") || "",
    };
}

export default function MatterAiWorkspace() {
    const { matterRef } = useParams();
    const navigate = useNavigate();

    const [accessAllowed, setAccessAllowed] = useState(false);
    const [accessChecked, setAccessChecked] = useState(false);
    const [actions, setActions] = useState([]);
    const [phase1aLimits, setPhase1aLimits] = useState({
        max_documents_per_run: 3,
        max_characters_per_document: 30_000,
    });
    const [activeActionKey, setActiveActionKey] = useState(null);
    const [selectedDocuments, setSelectedDocuments] = useState([]);
    const [instructionText, setInstructionText] = useState("");
    const [threadId, setThreadId] = useState(null);
    const [runResult, setRunResult] = useState(null);
    const [fetchWarnings, setFetchWarnings] = useState([]);
    const [isRunning, setIsRunning] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    // ── Initial access check + actions load
    useEffect(() => {
        const allowed = isWorkspaceEnabled();
        setAccessAllowed(allowed);
        setAccessChecked(true);
        if (!allowed) return;

        (async () => {
            try {
                const r = await axios.get(`${API}/aiworkspace/actions`, { headers: buildAuthHeaders() });
                setActions(r.data.actions || []);
                setPhase1aLimits(r.data.phase_1a_limits || phase1aLimits);
            } catch (err) {
                console.error("Failed to load action catalogue:", err);
                setErrorMessage("Could not load the action catalogue. Try refreshing.");
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Lazily create a thread on first run
    const ensureThreadExists = useCallback(async () => {
        if (threadId) return threadId;
        const r = await axios.post(
            `${API}/aiworkspace/thread`,
            { matter_ref: matterRef, title: `AI Workspace — ${matterRef}` },
            { headers: buildAuthHeaders() },
        );
        const newThreadId = r.data.thread.id;
        setThreadId(newThreadId);
        return newThreadId;
    }, [matterRef, threadId]);

    const activeAction = useMemo(
        () => actions.find(a => a.action_key === activeActionKey),
        [actions, activeActionKey],
    );

    const canRun = !!(
        activeActionKey &&
        selectedDocuments.length > 0 &&
        selectedDocuments.length <= phase1aLimits.max_documents_per_run &&
        (!activeAction?.requires_user_instruction || instructionText.trim().length > 0)
    );

    const handleRun = async () => {
        setIsRunning(true);
        setErrorMessage("");
        setRunResult(null);
        setFetchWarnings([]);
        try {
            const usedThreadId = await ensureThreadExists();
            const r = await axios.post(
                `${API}/aiworkspace/run`,
                {
                    thread_id: usedThreadId,
                    matter_ref: matterRef,
                    action_key: activeActionKey,
                    selected_documents: selectedDocuments,
                    user_instruction: instructionText.trim() || null,
                },
                { headers: buildAuthHeaders() },
            );
            setRunResult(r.data);
            setFetchWarnings(r.data.document_fetch_warnings || []);
            if (r.data.status !== "success") {
                setErrorMessage(r.data.error_message || `Run finished with status ${r.data.status}`);
            } else {
                toast.success("Run complete");
            }
        } catch (err) {
            setErrorMessage(formatError(err, "Unknown error"));
            toast.error("Run failed");
        } finally {
            setIsRunning(false);
        }
    };

    // ── Gate UI
    if (!accessChecked) {
        return (
            <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center" data-testid="aiworkspace-loading">
                <span className="text-sm text-slate-500">Checking access…</span>
            </div>
        );
    }
    if (!accessAllowed) {
        return (
            <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center"
                 data-testid="aiworkspace-not-available">
                <div className="text-center max-w-md p-6">
                    <Sparkles className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <h2 className="text-lg font-semibold text-white mb-1">AI Workspace is not available</h2>
                    <p className="text-sm text-slate-500">
                        Matter AI Workspace is in private alpha. If you think you should have access, talk to Vanessa.
                    </p>
                    <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}
                            data-testid="aiworkspace-go-back-btn">
                        <ChevronLeft className="w-4 h-4 mr-1" /> Go back
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F5F5F0]" data-testid="aiworkspace-root">
            {/* Header */}
            <div className="glass-nav sticky top-0 z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}
                                data-testid="aiworkspace-back-btn">
                            <ChevronLeft className="w-4 h-4 mr-1" /> Back
                        </Button>
                        <div>
                            <h1 className="text-lg font-bold text-white flex items-center gap-2">
                                <Sparkles className="w-5 h-5 text-teal" /> AI Workspace
                            </h1>
                            <p className="text-xs text-slate-500" data-testid="aiworkspace-matter-ref">
                                Matter: <span className="font-mono">{matterRef}</span>
                            </p>
                        </div>
                    </div>
                    <span className="text-xs text-status-amber italic" data-testid="aiworkspace-alpha-banner">
                        Private alpha — Phase 1a
                    </span>
                </div>
            </div>

            {/* Four-pane body */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left column: ActionBar + DocumentRail */}
                    <div className="space-y-6 lg:col-span-1">
                        <div className="bg-white rounded-md border border-navy-border p-4">
                            <h2 className="text-sm font-semibold text-white mb-3">Choose action</h2>
                            <ActionBar
                                actions={actions}
                                activeActionKey={activeActionKey}
                                onSelectAction={setActiveActionKey}
                                disabled={isRunning}
                            />
                        </div>
                        <div className="bg-white rounded-md border border-navy-border p-4">
                            <DocumentRail
                                selectedDocuments={selectedDocuments}
                                onChange={setSelectedDocuments}
                                maxCharactersPerDocument={phase1aLimits.max_characters_per_document}
                            />
                        </div>
                    </div>

                    {/* Right column: AnswerPane */}
                    <div className="lg:col-span-2 bg-white rounded-md border border-navy-border p-4 min-h-[480px]">
                        <AnswerPane
                            runResult={runResult}
                            isLoading={isRunning}
                            errorMessage={errorMessage}
                            fetchWarnings={fetchWarnings}
                        />
                    </div>
                </div>

                {/* Bottom: FollowUpInput */}
                <div className="mt-6 bg-white rounded-md border border-navy-border p-4">
                    <FollowUpInput
                        instructionText={instructionText}
                        onChange={setInstructionText}
                        requiresInstruction={!!activeAction?.requires_user_instruction}
                        isRunning={isRunning}
                        canRun={canRun}
                        onRun={handleRun}
                    />
                </div>
            </div>
        </div>
    );
}
